/* ──────────────────────────────────────────────────────────────────────────
   3 reason bans and their appeals (Jay's boss, 2026-10-15).

   The first ban on a phone is always a 3 reason ban, and the team appeals it
   straight away; it often comes back within minutes. The other accounts on
   that phone can follow it as connected bans, which are never appealed: they
   come back only if the 3 reason account does. So the 3 reason ban is the
   one to follow, with a status and dated updates.

   This replaced the 2026-10-12 rule that every account Instagram could not
   find had to be appealed and wait a day before Banned opened: connected
   bans are never appealed, so the wait was wrong for them.
   ────────────────────────────────────────────────────────────────────────── */

export type AppealState = "appealed" | "in_appeal" | "asked_for_id" | "came_back" | "not_back";

export const APPEAL_STATE: Record<AppealState, string> = {
  appealed: "Appealed",
  in_appeal: "Still in appeal",
  asked_for_id: "Asked for ID",
  came_back: "Came back",
  not_back: "Didn't come back",
};

/** In the order the buttons show. Appealed is set by the ban itself. */
export const APPEAL_STATES = Object.keys(APPEAL_STATE) as AppealState[];

/** Still waiting on Instagram: the ones someone needs to check on. */
export const appealOpen = (state: AppealState | null | undefined) => state !== "came_back" && state !== "not_back";

/** What decides where a ban stands: its own appeal, or the appeal of the 3 reason ban it came from. */
export type BanFacts = {
  status: string;
  banReason: "connected" | "three_reasons" | null;
  appealState: AppealState | null;
  /** The appeal state of the 3 reason ban a connected ban came from; undefined when none is linked. */
  causeAppealState?: AppealState | null;
};

const linked = (a: BanFacts) => a.banReason === "connected" && a.causeAppealState !== undefined;

/**
 * Lost for good: off the phone and in the Graveyard. A 3 reason ban marked
 * Didn't come back, and the connected bans that came from it (Jay,
 * 2026-10-15). Since 2026-10-21 also a connected ban with no 3 reason ban
 * linked: it is never appealed and has nothing to come back with, and the
 * boss said a ban that can't get back comes off the phone.
 */
export const offPhone = (a: BanFacts) =>
  a.status === "banned" &&
  ((a.banReason === "three_reasons" && a.appealState === "not_back") ||
    (a.banReason === "connected" && (!linked(a) || a.causeAppealState === "not_back")));

/**
 * Still in the balance: a 3 reason ban whose appeal is open, or a connected
 * ban whose 3 reason ban's appeal is. Best and worst setups leave these out
 * until the appeal is settled (Jay, 2026-10-15), since they may come back.
 */
export const awaitingAppeal = (a: BanFacts) =>
  a.status === "banned" && ((a.banReason === "three_reasons" && appealOpen(a.appealState)) || (linked(a) && appealOpen(a.causeAppealState)));
